import { getClauseChecklistScopeCount, readClauseChecklist } from "@/api";
import { FrontendPageContent } from "@/components/frontend/FrontendPageContent";
import { PageHeader } from "@/components/shared/layout/PageHeader";
import { LoadingIndicator } from "@/components/shared/LoadingIndicator";
import { Panel } from "@/components/shared/Panel";
import { SharedWithGroupLabel } from "@/components/shared/SharedWithGroupLabel";
import { HOME_ORG_PATH } from "@/config/constants";
import { useBreadcrumb } from "@/contexts/shared/breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Navigate, useParams } from "react-router";
import { ChecklistTopicsList } from "./components/clause-checklists/ChecklistTopicsList";

/**
 * The detail page for a clause checklist.
 * Displays checklist information, topics, and clauses.
 */
const ClauseChecklistDetail = () => {
    const { checklistId } = useParams();
    const { setBreadcrumbs } = useBreadcrumb();

    useDocumentTitle("Checklist");

    // Parse ID - will be NaN if invalid, which will cause query to fail
    const checklistIdNum = Number(checklistId);

    // Fetch checklist data - will throw to error boundary if ID is invalid
    const { data: checklist } = useSuspenseQuery({
        queryKey: ["frontend-clause-checklist", checklistIdNum],
        queryFn: () => readClauseChecklist(checklistIdNum),
    });

    // Fetch stats
    const { data: stats } = useQuery({
        queryKey: ["frontend-clause-checklist-times-applied", checklistIdNum],
        queryFn: () => getClauseChecklistScopeCount(checklistIdNum),
        enabled: !isNaN(checklistIdNum),
    });

    // Set breadcrumbs
    useEffect(() => {
        if (checklist) {
            setBreadcrumbs([
                { label: "Home", path: HOME_ORG_PATH },
                {
                    label: "Checklists",
                    path: "/organizations/checklists",
                    openInNewTab: true,
                },
                { label: checklist.name },
            ]);
        }
    }, [checklist, setBreadcrumbs]);

    // Redirect if no checklistId or invalid number (after hooks are called)
    if (!checklistId || isNaN(checklistIdNum)) {
        return <Navigate to="/organizations/clause-checklists" />;
    }

    // Safety check - shouldn't reach here if useSuspenseQuery worked correctly
    if (!checklist) {
        return <LoadingIndicator />;
    }

    return (
        <FrontendPageContent className="min-h-0 overflow-auto">
            <PageHeader
                title={checklist.name}
                leftContent={
                    <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        minWidth={0}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Created by InsureScope
                        </Typography>
                        <SharedWithGroupLabel
                            group={checklist.organization_group}
                            className="text-sm"
                        />
                        {stats && (
                            <Chip
                                label={`Applied ${(stats.scope_count ?? 0).toLocaleString()} times`}
                                size="small"
                                sx={{
                                    bgcolor: "primary.main",
                                    color: "primary.contrastText",
                                    fontWeight: 500,
                                    fontSize: "0.75rem",
                                }}
                            />
                        )}
                    </Stack>
                }
            />

            <Box sx={{ p: 3 }}>
                <Stack spacing={3}>
                    {/* Topics */}
                    <Panel title="Topics">
                        <ChecklistTopicsList checklistId={checklistIdNum} />
                    </Panel>
                </Stack>
            </Box>
        </FrontendPageContent>
    );
};

export default ClauseChecklistDetail;
